import CartInertiaController from '@/actions/App/Http/Controllers/CartInertiaController';
import { post, toErrorMessage } from '@/lib/axios';
import { router } from '@inertiajs/react';
import { toast } from 'sonner';

export interface RateAddServicePayload {
  service_mapping_code: string;
  service_name: string;
  quantity: number;
}

/** Matches the POST body of CartInertiaController.rateAdd() (service-cart-drawer form). */
export interface RateAddPayload {
  rate: string;
  site: string;
  date: string;
  services: RateAddServicePayload[];
}

export type PendingCartAction = {
  type: 'add' | 'buyNow';
  payload: RateAddPayload;
};

interface CartResponseItem {
  rowId: string;
  options?: {
    rate_mapping_code?: string;
    site_mapping_code?: string;
    date?: string;
    has_inventory?: string | boolean;
    [key: string]: unknown;
  };
}

interface RateAddResponseData {
  cart?: {
    items?: Record<string, CartResponseItem> | CartResponseItem[];
  };
}

const STORAGE_KEY = 'pending_cart_action';

export function savePendingCartAction(action: PendingCartAction): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(action));
  } catch (error) {
    console.error('Failed to save pending cart action:', error);
  }
}

export function loadPendingCartAction(): PendingCartAction | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as PendingCartAction;
    if ((parsed.type === 'add' || parsed.type === 'buyNow') && parsed.payload && Array.isArray(parsed.payload.services)) {
      return parsed;
    }
    return null;
  } catch (error) {
    console.error('Failed to load pending cart action:', error);
    return null;
  }
}

export function clearPendingCartAction(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear pending cart action:', error);
  }
}

/**
 * Executes a pending cart action (add to cart, or add + go to checkout).
 * Toasts + dispatches `cart-updated` on success. Returns true when the add succeeded.
 */
export async function executeCartAction(action: PendingCartAction): Promise<boolean> {
  try {
    const response = await post<RateAddResponseData>(CartInertiaController.rateAdd().url, action.payload);

    if (!response.success) {
      toast.error(toErrorMessage(response.message, 'Có lỗi xảy ra khi cập nhật giỏ hàng'));
      return false;
    }

    toast.success('Đã thêm vào giỏ hàng');
    window.dispatchEvent(new CustomEvent('cart-updated'));

    if (action.type === 'buyNow') {
      const rawItems = response.data?.cart?.items;
      const items: CartResponseItem[] = Array.isArray(rawItems) ? rawItems : Object.values(rawItems ?? {});

      const matchedItems = items.filter(
        (item) =>
          item.options?.rate_mapping_code === action.payload.rate &&
          item.options?.site_mapping_code === action.payload.site &&
          item.options?.date === action.payload.date,
      );

      // Session-based services need a time slot picked on the cart page first.
      if (matchedItems.some((item) => Boolean(item.options?.has_inventory))) {
        toast('Vui lòng chọn khung giờ cho dịch vụ');
        router.visit('/cart');
        return true;
      }

      const rowIds = matchedItems.map((item) => item.rowId).filter(Boolean);
      sessionStorage.setItem('checkout_items', JSON.stringify(rowIds));
      router.visit('/checkout');
    }

    return true;
  } catch (error) {
    console.error('Cart action error:', error);
    const message = error instanceof Error ? error.message : (error as { message?: string })?.message;
    toast.error(message || 'Có lỗi xảy ra khi cập nhật giỏ hàng');
    return false;
  }
}
